import { useState } from 'react'
import Modal from '../../../../../components/modal/Modal'
import { Icon } from '@iconify/react'
import { post, put } from '../../../../../utils/Api'
import { useGlobalContext } from '../../../../../utils/Context'
import FormLoader from '../../../../../components/loader/FormLoader'
import '../../../../../components/tags/tags.scss'
import type { Tag } from '../../../../../components/tags/TagChips'

// Mirrors TAG_COLOURS in the Tag model — a fixed palette rather than a free
// picker, so chips stay legible against the portal's chip styling.
const COLOURS = [
  '#E5484D',
  '#F76808',
  '#FFB224',
  '#46A758',
  '#12A594',
  '#0091FF',
  '#3E63DD',
  '#8E4EC6',
  '#E93D82',
  '#7C7C7C',
]

type Props = {
  setShowForm: (show: boolean) => void
  onSaved: () => void
  /** The tag being edited, or null to create a new one. */
  selectedTag: Tag | null
}

function TagForm({ setShowForm, onSaved, selectedTag }: Props) {
  const { notify } = useGlobalContext()
  const [loading, setLoading] = useState(false)
  const [name, setName] = useState(selectedTag?.name || '')
  const [colour, setColour] = useState(selectedTag?.colour || COLOURS[9])

  const submit = () => {
    const trimmed = name.trim()
    if (trimmed.length < 2) {
      return notify('warn', 'Tag name must be at least 2 characters')
    }
    if (trimmed.length > 40) {
      return notify('warn', 'Tag name must be 40 characters or fewer')
    }

    setLoading(true)
    const request = selectedTag
      ? put(`admin/tags/${selectedTag._id}`, { name: trimmed, colour })
      : post('admin/tags', { name: trimmed, colour }, null)

    request
      .then((result) => {
        if (result.status >= 200 && result.status <= 201) {
          notify('success', result.message)
          setShowForm(false)
          onSaved()
        } else {
          notify('error', result.message)
        }
        setLoading(false)
      })
      .catch(() => {
        notify('error', 'Something went wrong')
        setLoading(false)
      })
  }

  return (
    <Modal>
      <div className='left-modal'>
        <div className='modal-top flex justify-between items-center'>
          <h2>{selectedTag ? 'Edit Tag' : 'Create Tag'}</h2>
          <div className='close flex items-center justify-center'>
            <Icon icon='line-md:close' onClick={() => setShowForm(false)} />
          </div>
        </div>
        <form
          className='form'
          onSubmit={(e) => {
            e.preventDefault()
            submit()
          }}
        >
          <div className='input-card'>
            <p>Name</p>
            <input
              type='text'
              name='name'
              required
              maxLength={40}
              placeholder='e.g. Fragile'
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className='input-card'>
            <p>Colour</p>
            <div className='tag-swatches'>
              {COLOURS.map((option) => (
                <div
                  key={option}
                  className={`tag-swatch ${colour === option ? 'selected' : ''}`}
                  style={{ background: option }}
                  onClick={() => setColour(option)}
                  title={option}
                >
                  <Icon icon='mdi:check-bold' />
                </div>
              ))}
            </div>
          </div>
          <div className='input-card'>
            <p>Preview</p>
            <div className='tag-preview-box'>
              <span
                className='tag-chip'
                style={{
                  background: `${colour}1A`,
                  color: colour,
                  border: `1px solid ${colour}55`,
                }}
              >
                {name.trim() || 'Tag name'}
              </span>
            </div>
          </div>
          <div className='input-card'>
            <br />
            {loading ? (
              <FormLoader position='center' />
            ) : (
              <button type='submit'>{selectedTag ? 'Save' : 'Create'}</button>
            )}
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default TagForm
